/**
 * useCheckout Composable
 * Simple adapter for checkout operations
 * Delegates all business logic to store actions
 * Compatible with Vue 2
 */

import {
  getPaymentDetails,
  isDejavooRetryableTokenError
} from "@/services/paymentService";
import { loadSmsConsent, saveSmsConsent } from "@/services/localStorageService";
import { validateCheckoutForm } from "./useCheckoutValidation";
import { calculateGrandTotal } from "@/helpers/orderHelpers";

function getChargeAmountCents(totals) {
  return Math.round(
    (Number(totals.baseTotal.toFixed(2)) +
      Number(totals.convenienceTotal.toFixed(2))) *
      100
  );
}

function formatCurrencyAmount(cents, formatPrice) {
  if (typeof formatPrice === "function") {
    return formatPrice(cents / 100);
  }
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Composable for managing checkout operations
 * @param {Object} store - Vuex store instance
 * @param {Object} router - Vue router instance
 * @param {Object} componentState - Component's reactive state object (from data())
 * @returns {Object} Checkout methods and computed values
 */
export function useCheckout(store, router, componentState) {
  if (!store) {
    console.warn("useCheckout: Store instance is required");
    return {
      calculateTotals: () => ({}),
      isValid: false,
      submitOrder: async () => {},
      loadSmsConsent: () => {},
      saveSmsConsent: () => {}
    };
  }

  if (!componentState) {
    console.warn("useCheckout: Component state is required");
    return {
      calculateTotals: () => ({}),
      isValid: false,
      submitOrder: async () => {},
      loadSmsConsent: () => {},
      saveSmsConsent: () => {}
    };
  }

  /**
   * Calculates checkout totals
   * @param {Object} params
   * @param {number} params.subtotal - Subtotal
   * @param {number} params.tipAmount - Tip amount
   * @param {number} params.totalTax - Total tax
   * @param {Object} params.paymentMethod - Payment method object
   * @returns {Object} Totals object
   */
  function calculateTotals({ subtotal, tipAmount, totalTax, paymentMethod }) {
    const safeSubtotal = Number(subtotal || 0);
    const safeTip = Number(tipAmount || 0);
    const safeTax = Number(totalTax || 0);

    // Get inclusive tax setting from store
    const isTaxInclusive =
      store.getters.isStoreTaxInclusive ||
      (store.getters.getStore &&
        store.getters.getStore.tax_option === "inclusive") ||
      false;

    // Base total (subtotal + tax + tip)
    // Use centralized helper for grand total calculation
    const grandTotalBase = calculateGrandTotal(
      safeSubtotal,
      safeTax,
      isTaxInclusive
    );
    const baseTotal = grandTotalBase + safeTip;

    // Convenience fee (surcharge)
    let convenienceTotal = 0;
    if (paymentMethod && paymentMethod.surcharge) {
      const surcharge = Number(paymentMethod.surcharge) || 0;
      convenienceTotal = baseTotal * (surcharge / 100);
    }

    // Grand total
    const grandTotal = baseTotal + convenienceTotal;

    return {
      baseTotal,
      convenienceTotal,
      grandTotal
    };
  }

  /**
   * Computed validation state
   */
  function getIsValid() {
    return validateCheckoutForm({
      customerDetails: componentState.details,
      paymentMethod: componentState.currentPaymentMethod,
      cardDetails: componentState.cardDetails
    });
  }

  /**
   * Gets current payment method from available methods
   */
  function getCurrentPaymentMethod(
    availablePaymentMethods,
    selectedPaymentType
  ) {
    if (!availablePaymentMethods || !availablePaymentMethods.length) {
      return null;
    }
    return (
      availablePaymentMethods.find(
        (m) => String(m.id) === String(selectedPaymentType)
      ) || null
    );
  }

  /**
   * Submits the checkout order
   * Delegates to store action for business logic
   */
  async function submitCheckoutOrder({
    subtotal,
    totalTax,
    tipAmount,
    customerDetails,
    smsConsent,
    paymentTypeId,
    paymentMethod,
    cardDetails,
    stripeFormRef,
    dejavooFormRef
  }) {
    const displayedTotals = calculateTotals({
      subtotal,
      tipAmount,
      totalTax,
      paymentMethod
    });
    const displayedAmountCents = getChargeAmountCents(displayedTotals);

    await store.dispatch("order/applyPromotions");
    store.commit("order/RECALCULATE", { rootGetters: store.getters });

    const currentSubtotal =
      store.getters.subtotal != null ? store.getters.subtotal : subtotal;
    const currentTax =
      store.getters.getTotalTax != null ? store.getters.getTotalTax : totalTax;

    // Calculate totals up front. Dejavoo charges in the browser, so it needs the
    // exact amount; biyo-v2 recomputes the same value to verify the charge.
    const totals = calculateTotals({
      subtotal: currentSubtotal,
      tipAmount,
      totalTax: currentTax,
      paymentMethod
    });
    const amountCents = getChargeAmountCents(totals);

    // If the recalculation changed the total (stale cart state, promotions
    // loaded late), proceed with the corrected amount. The displayed total is
    // driven by the same store getters, so the UI updates to match what gets
    // charged. Log it so we can see how often this happens in production.
    if (Math.abs(amountCents - displayedAmountCents) > 1) {
      console.warn(
        `[Checkout] Cart total corrected before payment: ${formatCurrencyAmount(
          displayedAmountCents,
          store.getters.formatPrice
        )} -> ${formatCurrencyAmount(amountCents, store.getters.formatPrice)}`
      );
    }

    // Get payment details (service call only)
    let paymentDetails = null;
    if (paymentMethod && paymentMethod.card_processing) {
      try {
        paymentDetails = await getPaymentDetails({
          paymentMethod,
          cardDetails,
          stripeForm: stripeFormRef,
          dejavooForm: dejavooFormRef
        });
      } catch (error) {
        const rawMessage = error?.message || "Unable to process payment";
        const message = isDejavooRetryableTokenError(rawMessage)
          ? "We couldn't complete your payment right now. Please try again."
          : rawMessage;
        throw {
          message,
          isPaymentError: true
        };
      }
    }

    // Delegate to store action (all business logic there)
    await store.dispatch("order/submitOrder", {
      customer: customerDetails,
      smsConsent,
      paymentTypeId,
      convenienceTotal: totals.convenienceTotal,
      paymentDetails,
      tipAmount
    });

    // Save SMS consent (localStorage only)
    saveSmsConsent(smsConsent);
  }

  /**
   * Loads SMS consent from localStorage
   */
  function loadSmsConsentFromStorage() {
    componentState.smsConsent = loadSmsConsent();
  }

  /**
   * Saves SMS consent to localStorage
   */
  function saveSmsConsentToStorage() {
    saveSmsConsent(componentState.smsConsent);
  }

  /**
   * Clears checkout state after successful order
   */
  function clearCheckoutState() {
    componentState.isSubmitting = false;
    componentState.isSuccess = true;
    componentState.errorMessage = "";
  }

  /**
   * Handles checkout errors
   */
  function handleCheckoutError(error) {
    let errorMessage =
      error?.data ||
      error?.message ||
      "Something went wrong. Please try again.";

    // Handle specific payment errors
    if (error?.status === 400) {
      if (error.data === "Credit card has expired.") {
        errorMessage = "Credit card has expired.";
      } else if (
        error.data === "Invalid Card Number (3)" ||
        error.data?.includes("Card Number")
      ) {
        errorMessage = "Invalid card number";
      } else {
        errorMessage = error.data || "Invalid card details";
      }
    }

    componentState.errorMessage = errorMessage;
    componentState.isSubmitting = false;
  }

  return {
    // Methods
    calculateTotals,
    getIsValid,
    getCurrentPaymentMethod,
    submitCheckoutOrder,
    loadSmsConsentFromStorage,
    saveSmsConsentToStorage,
    clearCheckoutState,
    handleCheckoutError
  };
}
